import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMoney, formatDateTime } from '@/lib/format';
import {
  PAYMENT_LABELS,
  invoiceStatusMeta,
  INVOICE_ACTION_LABELS,
  type InvoiceAction,
  type Sale,
  type SaleItem,
} from '@/lib/types';
import {
  loadInvoiceDetail,
  refundInvoice,
  voidInvoice,
  type RefundLine,
} from '@/hooks/useInvoiceAdmin';

interface InvoiceDetailModalProps {
  saleId: string;
  currencySymbol: string;
  onClose: () => void;
  onDone: () => void;
}

type ConfirmKind = 'refund_full' | 'refund_partial' | 'void' | 'delete' | null;

export default function InvoiceDetailModal({
  saleId,
  currencySymbol,
  onClose,
  onDone,
}: InvoiceDetailModalProps) {
  const [sale, setSale] = useState<Sale | null>(null);
  const [items, setItems] = useState<SaleItem[]>([]);
  const [actions, setActions] = useState<InvoiceAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Record<string, number>>({});
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await loadInvoiceDetail(saleId);
    if (res.error) {
      setError(res.error);
    } else {
      setSale(res.sale);
      setItems(res.items);
      setActions(res.actions);
    }
    setLoading(false);
  }, [saleId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const statusMeta = sale ? invoiceStatusMeta(sale.status) : null;
  const isClosed = sale ? ['refunded', 'voided', 'deleted'].includes(sale.status) : false;

  const refundableItems = useMemo(
    () =>
      items.map((it) => ({
        item: it,
        refundable: Number(it.quantity || 0) - Number(it.returned_quantity || 0),
        unitPrice: Number(it.subtotal || 0) / Math.max(Number(it.quantity || 1), 1),
      })),
    [items]
  );

  const selectedLines = useMemo<RefundLine[]>(
    () =>
      Object.entries(selected)
        .filter(([, qty]) => qty > 0)
        .map(([saleItemId, quantity]) => ({ saleItemId, quantity })),
    [selected]
  );

  const selectedTotal = useMemo(() => {
    let total = 0;
    for (const line of selectedLines) {
      const found = refundableItems.find((r) => r.item.id === line.saleItemId);
      if (found) total += found.unitPrice * line.quantity;
    }
    return total;
  }, [selectedLines, refundableItems]);

  const toggleItem = (item: SaleItem, refundable: number) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = refundable;
      return next;
    });
  };

  const setQty = (itemId: string, value: number, max: number) => {
    const clamped = Math.max(0, Math.min(max, Math.floor(Number.isFinite(value) ? value : 0)));
    setSelected((prev) => {
      const next = { ...prev };
      if (clamped <= 0) delete next[itemId];
      else next[itemId] = clamped;
      return next;
    });
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  };

  const handleConfirm = async () => {
    if (!sale) return;
    setProcessing(true);
    let res: { error: string | null };

    if (confirm === 'refund_full') {
      res = await refundInvoice(sale.id, [], reason, true);
    } else if (confirm === 'refund_partial') {
      res = await refundInvoice(sale.id, selectedLines, reason, false);
    } else if (confirm === 'void') {
      res = await voidInvoice(sale.id, reason, 'void');
    } else if (confirm === 'delete') {
      res = await voidInvoice(sale.id, reason, 'delete');
    } else {
      res = { error: null };
    }

    setProcessing(false);
    if (res.error) {
      showToast(res.error);
      setConfirm(null);
      return;
    }
    setConfirm(null);
    setSelected({});
    setReason('');
    setConfirm(null);
    await load();
    onDone();
    showToast('Operación realizada correctamente.');
  };

  const confirmInfo: Record<Exclude<ConfirmKind, null>, { title: string; body: string; cta: string }> = {
    refund_full: {
      title: '¿Deseas realizar el reembolso de esta factura?',
      body: `Se devolverá el importe completo y se restaurarán los productos al inventario.`,
      cta: 'CONFIRMAR REEMBOLSO',
    },
    refund_partial: {
      title: '¿Confirmar reembolso parcial?',
      body: `Se devolverá solo lo seleccionado y se restaurarán esas unidades al inventario.`,
      cta: 'CONFIRMAR REEMBOLSO',
    },
    void: {
      title: '¿Anular esta factura?',
      body: 'La factura quedará como ANULADA, se restaurará el inventario y no contará en las ganancias.',
      cta: 'CONFIRMAR ANULACIÓN',
    },
    delete: {
      title: '¿Eliminar esta factura?',
      body: 'La factura quedará como ELIMINADA (no se borra físicamente), se conserva en auditoría y no contará como venta válida.',
      cta: 'CONFIRMAR ELIMINACIÓN',
    },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-3xl my-6 rounded-lg bg-background-50 border border-background-200">
        {/* Encabezado */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-background-200">
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              Factura {sale?.invoice_number ?? ''}
            </h3>
            <p className="text-xs text-foreground-500">{sale ? formatDateTime(sale.created_at) : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            {statusMeta && (
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${statusMeta.badge}`}>
                {statusMeta.label}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 transition cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-9 h-9 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
            </div>
          ) : error ? (
            <div className="rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-foreground-700">
              {error}
            </div>
          ) : sale ? (
            <>
              {/* Datos */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <InfoCell label="Cliente" value={sale.customer_name || 'Cliente general'} />
                <InfoCell label="Cajero" value={sale.cashier_name || '—'} />
                <InfoCell label="Sucursal" value={sale.branch?.name || '—'} />
                <InfoCell
                  label="Método de pago"
                  value={PAYMENT_LABELS[sale.payment_method] || sale.payment_method}
                />
              </div>

              {/* Items con selección para reembolso parcial */}
              <div className="rounded-lg border border-background-200 overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="text-foreground-600 text-left border-b border-background-200">
                    <tr>
                      <th className="py-2 px-3 font-medium w-10"></th>
                      <th className="py-2 px-3 font-medium">Producto</th>
                      <th className="py-2 px-3 font-medium text-center">Vend.</th>
                      <th className="py-2 px-3 font-medium text-center">Devuelto</th>
                      <th className="py-2 px-3 font-medium text-center">A devolver</th>
                      <th className="py-2 px-3 font-medium text-right">Precio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-100">
                    {refundableItems.map(({ item, refundable, unitPrice }) => (
                      <tr key={item.id}>
                        <td className="py-2 px-3">
                          <input
                            type="checkbox"
                            disabled={isClosed || refundable <= 0}
                            checked={Boolean(selected[item.id])}
                            onChange={() => toggleItem(item, refundable)}
                            className="w-4 h-4 accent-primary-500 cursor-pointer"
                          />
                        </td>
                        <td className="py-2 px-3 text-foreground-900">{item.product_name}</td>
                        <td className="py-2 px-3 text-center text-foreground-700">{item.quantity}</td>
                        <td className="py-2 px-3 text-center text-foreground-500">
                          {Number(item.returned_quantity || 0)}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <input
                            type="number"
                            min={0}
                            max={refundable}
                            disabled={isClosed || refundable <= 0}
                            value={selected[item.id] ?? 0}
                            onChange={(e) => setQty(item.id, Number(e.target.value), refundable)}
                            className="w-16 rounded-md border border-background-300 bg-background-50 px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-50"
                          />
                        </td>
                        <td className="py-2 px-3 text-right text-foreground-700">
                          {formatMoney(unitPrice, currencySymbol)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totales */}
              <div className="rounded-lg border border-background-200 bg-background-100/60 p-4 space-y-1.5 text-sm">
                <Row label="Subtotal" value={formatMoney(Number(sale.subtotal), currencySymbol)} />
                <Row label="Descuentos" value={`-${formatMoney(Number(sale.discount), currencySymbol)}`} />
                <Row label="Impuestos" value={formatMoney(Number(sale.tax), currencySymbol)} />
                <div className="flex justify-between border-t border-background-200 pt-2">
                  <span className="font-heading font-semibold text-foreground-950">Total</span>
                  <span className="font-heading text-lg font-bold text-foreground-950">
                    {formatMoney(Number(sale.total), currencySymbol)}
                  </span>
                </div>
                {Number(sale.refund_total) > 0 && (
                  <Row
                    label="Reembolsado"
                    value={`-${formatMoney(Number(sale.refund_total), currencySymbol)}`}
                    danger
                  />
                )}
              </div>

              {/* Motivo */}
              {!isClosed && (
                <div>
                  <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Motivo / observación
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={500}
                    rows={2}
                    placeholder="Ej: producto defectuoso, error de cobro, faltante..."
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  />
                </div>
              )}

              {/* Acciones */}
              {!isClosed && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => setConfirm('refund_full')}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
                  >
                    <i className="ri-refund-2-line" /> Solicitar reembolso
                  </button>
                  <button
                    onClick={() => setConfirm('refund_partial')}
                    disabled={selectedLines.length === 0}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-2.5 text-sm font-semibold text-primary-800 whitespace-nowrap transition disabled:opacity-50 cursor-pointer"
                  >
                    <i className="ri-checkbox-multiple-line" /> Reembolso parcial ({selectedLines.length})
                  </button>
                  <button
                    onClick={() => setConfirm('void')}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                  >
                    <i className="ri-forbid-2-line" /> Anular
                  </button>
                  <button
                    onClick={() => setConfirm('delete')}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-accent-300 px-4 py-2.5 text-sm font-semibold text-accent-800 hover:bg-accent-50 whitespace-nowrap transition cursor-pointer"
                  >
                    <i className="ri-delete-bin-6-line" /> Eliminar
                  </button>
                </div>
              )}

              {/* Historial */}
              {actions.length > 0 && (
                <div>
                  <h4 className="font-heading font-semibold text-foreground-950 text-sm">
                    Historial de acciones
                  </h4>
                  <ul className="mt-2 space-y-2">
                    {actions.map((a) => (
                      <li
                        key={a.id}
                        className="rounded-md border border-background-200 bg-background-100/60 px-3 py-2 text-xs text-foreground-700"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-foreground-900">
                            {INVOICE_ACTION_LABELS[a.action] || a.action}
                          </span>
                          <span>{formatDateTime(a.created_at)}</span>
                        </div>
                        <div className="mt-0.5">
                          {formatMoney(Number(a.amount), currencySymbol)} · {a.user_name || '—'}
                        </div>
                        {a.reason && <div className="mt-0.5 italic">Motivo: {a.reason}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-foreground-600">Factura no encontrada.</p>
          )}
        </div>
      </div>

      {/* Confirmación */}
      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirm(null)} aria-hidden="true" />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-background-50 border border-background-200 p-6">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              {confirmInfo[confirm].title}
            </h3>
            <p className="mt-2 text-sm text-foreground-700">{confirmInfo[confirm].body}</p>

            <div className="mt-4 rounded-md border border-background-200 bg-background-100/60 p-3 text-sm space-y-1">
              {confirm === 'refund_partial' ? (
                <>
                  {selectedLines.map((l) => {
                    const found = refundableItems.find((r) => r.item.id === l.saleItemId);
                    if (!found) return null;
                    return (
                      <div key={l.saleItemId} className="flex justify-between">
                        <span className="text-foreground-700">
                          {found.item.product_name} × {l.quantity}
                        </span>
                        <span className="font-medium text-foreground-900">
                          {formatMoney(found.unitPrice * l.quantity, currencySymbol)}
                        </span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between border-t border-background-200 pt-1.5">
                    <span className="font-semibold text-foreground-950">Monto a devolver</span>
                    <span className="font-bold text-foreground-950">
                      {formatMoney(selectedTotal, currencySymbol)}
                    </span>
                  </div>
                </>
              ) : confirm === 'refund_full' ? (
                <div className="flex justify-between">
                  <span className="font-semibold text-foreground-950">Monto a devolver</span>
                  <span className="font-bold text-foreground-950">
                    {formatMoney(
                      Number(sale?.total || 0) - Number(sale?.refund_total || 0),
                      currencySymbol
                    )}
                  </span>
                </div>
              ) : (
                sale && (
                  <div className="flex justify-between">
                    <span className="text-foreground-700">Factura {sale.invoice_number}</span>
                    <span className="font-medium text-foreground-900">
                      {formatMoney(Number(sale.total), currencySymbol)}
                    </span>
                  </div>
                )
              )}
            </div>

            <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={() => setConfirm(null)}
                className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                disabled={processing}
                className="rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
              >
                {processing ? 'Procesando...' : confirmInfo[confirm].cta}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] rounded-md bg-foreground-950 text-background-50 px-4 py-2.5 text-sm font-medium">
          {toast}
        </div>
      )}
    </div>
  );
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-background-200 bg-background-100/60 px-3 py-2">
      <p className="text-xs text-foreground-500">{label}</p>
      <p className="mt-0.5 font-medium text-foreground-900 truncate">{value}</p>
    </div>
  );
}

function Row({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-foreground-600">{label}</span>
      <span className={danger ? 'font-medium text-accent-700' : 'font-medium text-foreground-900'}>
        {value}
      </span>
    </div>
  );
}